import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import apolloClient from '@/lib/apollo-client';
import { VERIFY_EMAIL_MUTATION } from '@/lib/graphql/mutations';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying your email...');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Extract token from URL
        const searchParams = new URLSearchParams(location.search);
        const token = searchParams.get('token');

        if (!token) {
          setVerificationStatus('error');
          setMessage('Invalid verification link. No token provided.');
          return;
        }

        // Call the verification mutation
        const { data } = await apolloClient.mutate({
          mutation: VERIFY_EMAIL_MUTATION,
          variables: { token }
        });

        if (data.verifyEmail.success) {
          setVerificationStatus('success');
          setMessage(data.verifyEmail.message || 'Your email has been successfully verified!');
          
          toast({
            title: 'Email Verified',
            description: 'Your account has been successfully verified.',
            variant: 'default',
          });
        } else {
          setVerificationStatus('error');
          setMessage(data.verifyEmail.message || 'Email verification failed.');
          
          toast({
            title: 'Verification Failed',
            description: data.verifyEmail.message || 'Failed to verify your email. Please try again.',
            variant: 'destructive',
          });
        }
      } catch (error) {
        console.error('Email verification error:', error);
        setVerificationStatus('error');
        setMessage('An error occurred during verification. The link may have expired.');
        
        toast({
          title: 'Verification Error',
          description: 'Failed to verify your email. The link may have expired.',
          variant: 'destructive',
        });
      }
    };

    verifyEmail();
  }, [location.search, toast]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Email Verification</CardTitle>
          <CardDescription>
            {verificationStatus === 'loading' ? 'Verifying your email address...' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-6">
          {verificationStatus === 'loading' && (
            <div className="text-center space-y-4">
              <div className="mx-auto bg-blue-100 rounded-full p-3 w-16 h-16 flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
              </div>
              <p className="text-lg font-medium">Verifying your email...</p>
              <p className="text-sm text-muted-foreground">This may take a moment.</p>
            </div>
          )}

          {verificationStatus === 'success' && (
            <div className="text-center space-y-4">
              <div className="mx-auto bg-green-100 rounded-full p-3 w-16 h-16 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-lg font-medium">Email Verified!</p>
              <p className="text-sm text-muted-foreground">{message}</p>
            </div>
          )}

          {verificationStatus === 'error' && (
            <div className="text-center space-y-4">
              <div className="mx-auto bg-red-100 rounded-full p-3 w-16 h-16 flex items-center justify-center">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <p className="text-lg font-medium">Verification Failed</p>
              <p className="text-sm text-muted-foreground">{message}</p>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          {verificationStatus !== 'loading' && (
            <Button 
              onClick={() => navigate('/signin')} 
              className="w-full"
            >
              {verificationStatus === 'success' ? 'Sign In' : 'Back to Sign In'}
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

export default VerifyEmail;